import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  createBudgetLine,
  deleteBudgetLine,
  listBudget,
  updateBudgetLine,
  type BudgetLine,
  type BudgetView,
} from '../api.js';
import { formatMoney, moneyForInput } from '../labels.js';

/**
 * The budget (CM-02).
 *
 * The whole section is money, so a caller without financial access on this
 * project is refused the call itself and sees one honest line of text — not an
 * empty table, which would say the project has no budget.
 *
 * Every figure is a decimal STRING from the database column to the screen.
 * `formatMoney` groups it by string surgery and never calls `Number()`, because
 * a float in the middle of that journey is how 1,250,000.00 quietly becomes
 * 1,249,999.99.
 */

interface Props {
  readonly projectId: string;
  /** The project's currency. Money labels say it; they never assume AED. */
  readonly currency: string;
  readonly can: { readonly edit: boolean };
  /** An operator support visit that did not ask for the money (ADR-041). */
  readonly visiting?: boolean;
}

export function Budget({ projectId, currency, can, visiting = false }: Props): JSX.Element {
  const [view, setView] = useState<BudgetView | null>(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await listBudget(projectId));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the budget.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<BudgetView>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Budget</h3>
      {view && !adding && can.edit && (
        <button type="button" className="inline" onClick={() => setAdding(true)}>
          Add a line
        </button>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">
          {visiting
            ? 'This visit cannot see money, so the budget is not shown.'
            : 'You do not have financial access on this project, so the budget is not shown.'}
        </p>
      </>
    );
  }

  if (!view) {
    return (
      <>
        {head}
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Loading the budget…</p>
        )}
      </>
    );
  }

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {adding && (
        <LineForm
          currency={currency}
          busy={busy}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => createBudgetLine(projectId, { ...input, category: input.category })))
              setAdding(false);
          }}
        />
      )}

      {editing && (
        <LineForm
          existing={view.lines.find((l) => l.id === editing)!}
          currency={currency}
          busy={busy}
          onCancel={() => {
            setEditing(null);
            setError(null);
          }}
          onSubmit={async (input) => {
            // The version this form was OPENED on. `view` is re-read after
            // every save, so this is the line as it stood when the form
            // appeared — which is exactly what the guard needs to compare.
            const version = view.lines.find((l) => l.id === editing)?.record_version;
            if (await run(() => updateBudgetLine(projectId, editing, input, version)))
              setEditing(null);
          }}
        />
      )}

      {view.lines.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No budget lines yet.</strong>
          </p>
          {/*
            An empty state must name an action the READER can take. A Company
            Admin holds `budget: view + export` and nothing else (`04`), so
            telling them to add a line offers something that is not there — the
            same mistake as a button that 403s, only quieter.

            "OR THE PROJECT MANAGER" WAS WRONG, and wrong in the worst
            direction: it named somebody who also cannot. `04` gives the PM
            `budget: fin('V')` — view, and only with financial access. By
            default `qs_commercial` is the ONLY role that may create a budget
            line; Company Admin and Director both hold `g('VX')`.

            So an admin setting up a company was sent to ask a colleague who
            could not have helped either, to satisfy a gate the Award stage
            cannot pass without — and on a new company there is no QS yet.
            Found on the first run through the product.

            It names the ROLES SCREEN rather than a person, because since
            ADR-035 the answer genuinely depends on the company: the QS is only
            the default, and any internal role can be given the cell. A role
            named here would be a fourth copy of the matrix and would go stale
            the moment a customer edits theirs.
          */}
          <p>
            The Award stage cannot be completed until there is a baseline budget above zero.{' '}
            {can.edit
              ? 'Add what was allowed at award, by category.'
              : 'By default only QS / Commercial may set one. Company → Roles decides who else in your company can.'}
          </p>
        </div>
      ) : (
        <table className="grid">
          <caption className="visually-hidden">
            The budget by category, with what was allowed at award against what is allowed now
          </caption>
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">At award</th>
              <th scope="col">Now</th>
              <th scope="col">Movement</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {view.lines.map((line) => (
              <tr key={line.id}>
                <td>
                  <strong>{line.category}</strong>
                  {line.description && <span className="tree-code"> {line.description}</span>}
                </td>
                <td data-label="At award" className="mono">{formatMoney(line.baseline_amount)}</td>
                <td data-label="Now" className="mono">
                  {formatMoney(line.revised_amount ?? line.baseline_amount)}
                  {line.revised_amount === null && line.baseline_amount !== null && (
                    <span className="budget-unrevised">Unrevised</span>
                  )}
                </td>
                <td data-label="Movement"
                  className={`mono${line.movement?.startsWith('-') ? '' : ' budget-up'}`}
                >
                  {line.movement === null
                    ? '—'
                    : `${line.movement.startsWith('-') ? '' : '+'}${formatMoney(line.movement)}`}
                </td>
                <td className="row-actions">
                  {can.edit && (
                    <>
                      <button
                        type="button"
                        className="link-button"
                        disabled={busy}
                        onClick={() => {
                          setError(null);
                          setAdding(false);
                          setEditing(line.id);
                        }}
                      >
                        Edit<span className="visually-hidden"> {line.category}</span>
                      </button>
                      <button
                        type="button"
                        className="link-button danger"
                        disabled={busy}
                        onClick={() => void run(() => deleteBudgetLine(projectId, line.id))}
                      >
                        Remove<span className="visually-hidden"> {line.category}</span>
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Total</th>
              <td data-label="At award" className="mono">
                <strong>
                  {view.currency} {formatMoney(view.baseline_total)}
                </strong>
              </td>
              <td data-label="Now" className="mono">
                <strong>
                  {view.currency} {formatMoney(view.revised_total)}
                </strong>
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      )}

      {view.lines.length > 0 && (
        <p className="hint">
          <strong>At award</strong> is what was allowed when the job was won.{' '}
          <strong>Now</strong> is what is allowed today — a line nobody has revised still stands
          at its award figure, and the total says so.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface FormValues {
  readonly category: string;
  readonly description: string | null;
  readonly baseline_amount: string | null;
  readonly revised_amount: string | null;
}

interface FormProps {
  readonly existing?: BudgetLine;
  readonly currency: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: FormValues) => void | Promise<void>;
}

function LineForm({ existing, currency, busy, onCancel, onSubmit }: FormProps): JSX.Element {
  const [category, setCategory] = useState(existing?.category ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [baseline, setBaseline] = useState(moneyForInput(existing?.baseline_amount));
  const [revised, setRevised] = useState(moneyForInput(existing?.revised_amount));

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!category.trim()) return;
    void onSubmit({
      category: category.trim(),
      description: description.trim() || null,
      baseline_amount: baseline.trim() || null,
      revised_amount: revised.trim() || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field-row">
        <div className="field">
          <label htmlFor="bud-category">Category</label>
          <input
            id="bud-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            maxLength={80}
            disabled={busy}
            placeholder="Joinery"
          />
        </div>
        <div className="field">
          <label htmlFor="bud-description">Description</label>
          <input
            id="bud-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            disabled={busy}
            placeholder="Reception, meeting rooms and tea points"
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="bud-baseline">Allowed at award ({currency})</label>
          <input
            id="bud-baseline"
            value={baseline}
            onChange={(e) => setBaseline(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            placeholder="1250000.00"
          />
        </div>
        <div className="field">
          <label htmlFor="bud-revised">Allowed now ({currency})</label>
          <input
            id="bud-revised"
            value={revised}
            onChange={(e) => setRevised(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            placeholder="Leave blank if unchanged"
          />
          <p className="hint">A blank line still stands at its award figure.</p>
        </div>
      </div>

      <p className="hint">
        Plain figures only — no commas, no currency symbol, and nothing negative. A credit belongs
        in a variation, which carries its own sign.
      </p>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !category.trim()}>
          {busy ? 'Saving…' : existing ? 'Save' : 'Add the line'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
